/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.buyButtons = this.section.querySelector('.quantity-submit-row');
        if (!this.buyButtons) return;

        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = Array.from(this.mainProductForm.querySelectorAll('.js-product-form')).find(
          (form) => !form.closest('sticky-atc')
        );
        if (!this.mainProductSubmitForm) return;
        this.submitForm = this.getSubmitForm();
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');
        this.buyNowButton = this.querySelector('[data-sticky-atc-buy-now]');
        this.status = this.querySelector('.sticky-atc__status');
        this.price = this.querySelector('.sticky-atc__price');
        this.changeMobile = this.querySelector('.sticky-atc__change-mobile');
        this.variantTrigger = this.section.querySelector('.ns-pdp-variant-row') || this.buyButtons;
        this.buyNowButton?.addEventListener('click', this.handleBuyNow.bind(this));

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

        this.updateImage();
        this.updateAddToCartButton();
        this.handleScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.mainProductSubmitForm?.querySelector('input[name="id"]')?.value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Clear copied data
        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        // Copy data
        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const trigger = isMobile ? this.variantTrigger : this.buyButtons;
        const selectedVariantAvailable = this.dataset.stickyAtcVariantAvailable === 'true';

        this.classList.toggle('sticky-atc--variant-unavailable', !selectedVariantAvailable);
        this.classList.toggle(
          'sticky-atc--out',
          !selectedVariantAvailable || trigger.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          !isMobile && window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        const variantId = this.mainProductSubmitForm?.querySelector('input[name="id"]')?.value;
        if (!variantId) {
          if (this.button) this.button.innerHTML = this.button.dataset.unavailableText;
          if (this.buyNowButton) {
            this.buyNowButton.disabled = true;
            this.buyNowButton.hidden = true;
          }
          if (this.changeMobile) this.changeMobile.hidden = false;
        }
      }

      onVariantChange(evt) {
        this.updateVariantState(evt.detail.variant);
      }

      formatMoney(cents) {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents);
        }

        if (window.ThemeRevenueEngine && typeof window.ThemeRevenueEngine.formatMoney === 'function') {
          return window.ThemeRevenueEngine.formatMoney(cents);
        }

        return `₹${(Number(cents || 0) / 100).toLocaleString('en-IN')}`;
      }

      updateVariantState(variant) {
        const hasVariant = !!variant;
        const available = !!(variant && variant.available);
        this.dataset.stickyAtcVariantAvailable = available ? 'true' : 'false';

        if (this.variantTitle && variant?.title) {
          this.variantTitle.textContent = variant.title;
          this.variantTitle.closest('.sticky-atc__details__variant')?.removeAttribute('hidden');
        }

        if (this.price && typeof variant?.price !== 'undefined') {
          this.price.textContent = this.formatMoney(variant.price);
        }

        if (this.status) {
          this.status.classList.toggle('sticky-atc__status--sold-out', hasVariant && !available);
          this.status.classList.toggle('sticky-atc__status--choose', !hasVariant);
          this.status.textContent = hasVariant ? (available ? 'Ready to order' : 'Sold Out') : 'Choose colour';
        }

        if (this.button?.tagName === 'BUTTON') {
          this.button.disabled = !available;
          this.button.textContent = available
            ? this.button.dataset.addToCartText
            : (hasVariant ? this.button.dataset.soldOutText : this.button.dataset.unavailableText);
        }

        if (this.buyNowButton) {
          this.buyNowButton.disabled = !available;
          this.buyNowButton.hidden = !available;
        }

        if (this.changeMobile) {
          this.changeMobile.hidden = available;
        }

        this.handleScroll();
      }

      async handleBuyNow(evt) {
        evt.preventDefault();

        const variantId = this.mainProductSubmitForm?.querySelector('input[name="id"]')?.value;
        if (!variantId || this.buyNowButton?.disabled) {
          theme.scrollToRevealElement(this.variantTrigger || this.mainProductForm);
          return;
        }

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        const shiprocketNode = this.section.querySelector('.product-dynamic-payment-button .shiprocket-headless[data-type="product"]');
        const shiprocketTarget = shiprocketNode?.querySelector('button, a, [role="button"], .sr-headless-checkout') || shiprocketNode;

        if (shiprocketTarget) {
          shiprocketTarget.click();
          return;
        }

        theme.scrollToRevealElement(this.buyButtons);
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
